/* eslint-disable @typescript-eslint/no-explicit-any */

import { animate, state, style, transition, trigger } from "@angular/animations";
import { Component, ElementRef, HostBinding, HostListener, Input, OnInit, Renderer2, ViewChild, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { easeQuadOut, hierarchy, select, tree, zoom, zoomIdentity } from "d3";
import { BehaviorSubject, finalize } from "rxjs";
import {
    InventoryGraphEdge,
    InventoryGraphNode,
    InventoryGraphNodeResource,
    InventoryGraphNodeType,
    InventoryResourceGraph,
    ResourceStatus,
} from "src/app/models/api/inventoryResource.model";
import { InventoryHttpService } from "src/app/services/httpServices/inventoryHttpService/inventory-http.service";
import { GraphHierarchyLink, GraphHierarchyNode, InventoryGraphMerged, InventoryGraphMergedNodesList, NodeSelection } from "./model";
import {
    addClipPath,
    countChildren,
    diameter,
    edgeRelationType,
    fullScreenBoundingBox,
    fullScreenMargin,
    imageTransform,
    isLinkInactive,
    isNodeInactive,
    maxInitialZoom,
    maxZoom,
    minZoom,
    nodeIconUrl,
    nodeTransform,
    pathCurve,
    pathTextTransform,
    radius,
    singleNodeHeight,
    textTransform,
} from "./utils";
import { emptySet } from "src/app/common/functionalUtils";
import { InventoryGraphNodeCategory } from "./enums";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { v4 as uuidv4 } from "uuid";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";
import { buildResourceTypeDisplayName } from "../../labels/utils";
import { lowerCase } from "lodash";
import { IssuesDataService } from "src/app/views/issues/issues.component.service";
@Component({
    animations: [
        trigger("fullscreen", [
            state(
                "yes",
                style({
                    visibility: "visible",
                    left: `${fullScreenMargin}px`,
                    top: `${fullScreenMargin}px`,
                    width: `calc(100vw - 2 * ${fullScreenMargin}px)`,
                    height: `calc(100vh - 2 * ${fullScreenMargin}px)`,
                    opacity: 1,
                }),
            ),
            state(
                "no",
                style({
                    visibility: "hidden",
                    left: "var(--left)",
                    top: "var(--top)",
                    width: "var(--width)",
                    height: "var(--height)",
                    opacity: 0.5,
                }),
            ),
            transition("yes => no", animate("0.2s ease-in")),
            transition("no => yes", animate("0.2s ease-out")),
        ]),
    ],
    selector: "app-relations-graph",
    templateUrl: "./relations-graph.component.html",
    styleUrls: ["./relations-graph.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class RelationsGraphComponent implements OnInit {
    @ViewChild("graph", { static: true }) private _graph: ElementRef;
    @ViewChild("popup") private _popup: ElementRef;
    @ViewChild("tooltipFullScreen") private _tooltipFullScreen: ElementRef;
    @ViewChild("tooltipSmallScreen") private _tooltipSmallScreen: ElementRef;

    @HostBinding("style.--left.px") public left = 0;
    @HostBinding("style.--top.px") public top = 0;
    @HostBinding("style.--width.px") public width = 0;
    @HostBinding("style.--height.px") public height = 0;

    @Input() public integrationId: string;
    @Input() public resourceId: string;
    @Input() public isIssueComponent: boolean;
    public isLoading$ = new BehaviorSubject(false);

    public isFullScreen = false;

    private _svgContainer: d3.Selection<SVGSVGElement, InventoryGraphNode, HTMLElement, any>;
    private _graphSvg: d3.Selection<SVGGElement, InventoryGraphNode, HTMLElement, any>;

    private _graphWidth = 0;
    private _graphHeight = 0;

    public cursorX = 0;
    public cursorY = 0;

    public mergedGraph: InventoryGraphMerged;
    public rootNode: InventoryGraphNode;
    private _virtualNode: InventoryGraphNode;
    private _inventoryGraphNodeTypeToCategory = {
        [InventoryGraphNodeType.resource]: InventoryGraphNodeCategory.resource,
        [InventoryGraphNodeType.orgWideAccess]: InventoryGraphNodeCategory.virtualNode,
        [InventoryGraphNodeType.publicAccess]: InventoryGraphNodeCategory.virtualNode,
        [InventoryGraphNodeType.m365copilot]: InventoryGraphNodeCategory.virtualNode,
    };

    constructor(
        private _inventoryHttpService: InventoryHttpService,
        private _router: Router,
        private _renderer: Renderer2,
        private _beautifyDisplayString: BeautifyDisplayStringPipe,
        private _issuesDataService: IssuesDataService,
        public featureFlagService: FeatureFlagsService,
    ) {}

    public ngOnInit(): void {
        this.isLoading$.next(true);
        this.mergedGraph = {
            mergedGraphDictionary: {},
        };
        if (!this.isIssueComponent) {
            this._inventoryHttpService
                .getInventoryResourceGraph(this.integrationId, this.resourceId, 2)
                .pipe(finalize(() => this.isLoading$.next(false)))
                .subscribe((graph) => this._onGraphLoaded(graph));
        } else {
            this._issuesDataService
                .getIssueRelationsGraph(this.resourceId)
                .pipe(finalize(() => this.isLoading$.next(false)))
                .subscribe((mockGraph) => this._onGraphLoaded(mockGraph));
        }
        this._updatePositionBindings(this._boundingBox());
    }
    @HostListener("document:mousemove", ["$event"])
    public onMouseMove(e: MouseEvent) {
        if (this.isFullScreen) {
            this.cursorX = e.clientX + 10 - fullScreenMargin;
            this.cursorY = e.clientY + 10 - fullScreenMargin;
            this._renderer.setStyle(this._tooltipFullScreen.nativeElement, "left", this.cursorX.toString() + "px");
            this._renderer.setStyle(this._tooltipFullScreen.nativeElement, "top", this.cursorY.toString() + "px");
        } else {
            this.cursorX = e.clientX + 10 - this._boundingBox().x;
            this.cursorY = e.clientY + 10 - this._boundingBox().y;
            if (this.cursorX > this._boundingBox().width - 150) {
                this.cursorX -= 150;
                this.cursorY += 10;
            }

            if (this.cursorY > this._boundingBox().height - 50) {
                this.cursorY -= 50;
            }
            this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, "left", this.cursorX.toString() + "px");
            this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, "top", this.cursorY.toString() + "px");
        }
    }
    private _boundingBox = () => this._graph.nativeElement.getBoundingClientRect();

    private _onGraphLoaded(graph: InventoryResourceGraph) {
        const nodeMap = graph.graphNodes.toMapBy((node) => node.resource.id);
        const distinctNodesMap: { [distinctId: string]: InventoryGraphNode } = {};
        const visited = emptySet<string>();

        function buildChildren(node: InventoryGraphNode, mergedGraph: InventoryGraphMerged): InventoryGraphNode[] {
            visited.add(node.resource.id);
            const edgeNode = (edge: InventoryGraphEdge) => {
                const newNode = {} as InventoryGraphNode;
                Object.assign(newNode, nodeMap.get(edge.targetResourceId));
                newNode.distinctId = uuidv4(); // When we get the nodes they don't have distinct ids, so we need to add them.
                distinctNodesMap[newNode.distinctId] = newNode; // We need to save the distinct nodes in a map so we can access them later
                return newNode;
            };
            node.distinctChildrenIds = [];
            node?.edges?.forEach((edge) => {
                edgeNode(edge)?.let((nodeInside) => {
                    nodeInside.parentRelationType = edgeRelationType(edge);
                    if (nodeInside.distinctId) node.distinctChildrenIds?.push(nodeInside.distinctId);
                });
            });

            // Recursively build children
            node.children = node?.distinctChildrenIds
                ?.map((distinctId) => distinctNodesMap[distinctId]!)
                ?.filter((child) => !visited.has(child.resource.id))
                ?.alsoForEach((child) => buildChildren(child, mergedGraph));

            // This part is adding the nodes to the merged graph (parent -> relationType -> resourceType -> hasChild -> {areMerged: boolean, nodes: InventoryGraphNode[]})
            mergedGraph.mergedGraphDictionary[node.resource.id] = {};
            node.children?.forEach((child) => {
                if (mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType] == null) {
                    mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType] = {};
                }
                if (mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type] == null) {
                    mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type] = {};
                }
                let hasChild = "false";
                if (child.children && child.children.length > 0) hasChild = "true";
                if (mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][hasChild] == null) {
                    if (hasChild === "true") {
                        mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][hasChild] = {
                            areMerged: false,
                            nodes: [],
                        };
                    } else {
                        mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][hasChild] = {
                            areMerged: true,
                            nodes: [],
                        };
                    }
                }
                mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][hasChild].nodes.push(child);
            });
            // End of adding nodes to the merged graph

            return node.children ?? [];
        }

        // We want to find the first node in the graph - in can be the root node or the access node
        let root = graph.graphNodes.find((node) => node.resource.id === this.resourceId);

        if (root == null) return;

        nodeMap.forEach((node) => {
            if (node.resource.id.startsWith("virtual_node")) this._virtualNode = node;
        });

        if (this._virtualNode) {
            // Check if we have a virtual node (a node that is not based on inventory resource)
            root = this._virtualNode;
            if (this._virtualNode.nodeType === InventoryGraphNodeType.publicAccess) root.iconUrl = "assets/images/common/globe-colored.svg";
            else if (this._virtualNode.nodeType === InventoryGraphNodeType.orgWideAccess)
                root.iconUrl = "assets/images/common/organization-colored.svg";
            else if (this._virtualNode.nodeType === InventoryGraphNodeType.m365copilot) root.iconUrl = "assets/images/logo/m365Copilot.svg";
        }

        root.children = buildChildren(root, this.mergedGraph); // Start building the graph from the first node recursively
        this.rootNode = root;
        this._buildGraph();
    }

    private _buildNewRootNode(rootNode: InventoryGraphNode) {
        const newRoot = {
            _links: rootNode._links,
            resource: rootNode.resource,
            displayName: rootNode.displayName,
            iconUrl: rootNode.iconUrl,
            edges: [],
            children: [],
            parentRelationType: rootNode.parentRelationType,
            nodeType: rootNode.nodeType,
        } as InventoryGraphNode;

        if (this.mergedGraph.mergedGraphDictionary[rootNode.resource.id] == null) {
            return newRoot;
        }

        Object.keys(this.mergedGraph.mergedGraphDictionary[rootNode.resource.id]).forEach((parentRelationType) => {
            Object.keys(this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType]).forEach((resourceType) => {
                Object.keys(this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType][resourceType]).forEach((hasChild) => {
                    const nodes: InventoryGraphMergedNodesList =
                        this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType][resourceType][hasChild];

                    if (hasChild === "true") {
                        nodes.nodes.forEach((node: InventoryGraphNode) => {
                            newRoot.children?.push(this._buildNewRootNode(node));
                        });
                    } else {
                        if (!nodes.areMerged || nodes.nodes.length === 1) {
                            nodes.nodes.forEach((node: InventoryGraphNode) => {
                                newRoot.children?.push(this._buildNewRootNode(node));
                            });
                        } else {
                            const newNode = {
                                _links: rootNode._links,
                                resource: {
                                    id: "merged-" + nodes.nodes.map((node) => node.resource.id).join("_"),
                                    status: ResourceStatus.active,
                                    category: nodes.nodes[0].resource.category,
                                    type: nodes.nodes[0].resource.type,
                                } as InventoryGraphNodeResource,
                                displayName:
                                    "More " +
                                    lowerCase(buildResourceTypeDisplayName(nodes.nodes[0].resource.type)) +
                                    "s (" +
                                    nodes.nodes.length +
                                    ")",
                                iconUrl: nodes.nodes[0].iconUrl,
                                edges: [],
                                children: [],
                                parentRelationType: nodes.nodes[0].parentRelationType,
                                nodeType: InventoryGraphNodeType.resource,
                            } as InventoryGraphNode;
                            newRoot.children?.push(newNode);
                        }
                    }
                });
            });
        });

        return newRoot;
    }

    private _buildGraph() {
        this._svgContainer = select<SVGGElement, InventoryGraphNode>(this._graph.nativeElement)
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("id", "graph-svg")
            .also(addClipPath);

        this._graphSvg = this._svgContainer.append("g");

        const newRoot = this._buildNewRootNode(this.rootNode);

        const root = hierarchy<InventoryGraphNode>(newRoot) as GraphHierarchyNode;
        const leaves = root.leaves();
        const maxDepth = leaves?.reduce((maxDepth, leaf) => (maxDepth = Math.max(leaf.depth, maxDepth)), 0) ?? 0;
        this._graphHeight = (leaves?.length ?? 0) * singleNodeHeight;
        this._graphWidth = maxDepth * 300;

        tree<InventoryGraphNode>()
            .separation((a, b) => countChildren(a) + countChildren(b))
            .size([this._graphHeight, this._graphWidth])(root);

        const nodes = this._graphSvg
            .selectAll<SVGGElement, GraphHierarchyNode>(".node")
            .data(root.descendants())
            .enter()
            .append("g")
            .attr("transform", nodeTransform)
            .classed("node", true);

        nodes.append("circle").classed("root-node", this._isRootNode).classed("highlight", true);

        nodes
            .append("image")
            .attr("href", nodeIconUrl)
            .attr("width", diameter)
            .attr("height", diameter)
            .attr("cursor", "pointer")
            .attr("transform", imageTransform)
            .attr("clip-path", "url(#clip-circle)")
            .classed("inactive-node", isNodeInactive)
            .classed("node-image", true)
            .on("click", (event, node) => this._onNodeClicked(event, node))
            .on("mouseover", (_, node) => this._handleNodeMouseOver(nodes, node, true))
            .on("mouseout", (_, node) => this._handleNodeMouseOver(nodes, node, false));
        nodes
            .append("text")
            .html((node) =>
                this._addResourceTypeToDisplayNameIfNeeded(this._changeDisplayNameToBeMultipleLinesIfTooLong(node.data.displayName), node.data),
            )
            .attr("text-anchor", "middle")
            .attr("transform", textTransform)
            .on("mouseover", (_, node) => this._handleNodeMouseOverTooltip(node.data.displayName))
            .on("mouseout", () => this._handleNodeMouseOutTooltip());
        const link = this._graphSvg.selectAll<SVGPathElement, GraphHierarchyLink>(".link").data(root.links()).enter().append("g");

        link.append("path")
            .attr("d", pathCurve as never)
            .classed("link", true)
            .classed("disabled-link", isLinkInactive as never);

        link.append("text")
            .html((link) => this._changeDisplayNameToBeMultipleLinesIfTooLong(link.target.data.parentRelationType))
            .attr("text-anchor", "middle")
            .attr("transform", pathTextTransform as never)
            .on("mouseover", (_, link) => this._handleNodeMouseOverTooltip(link.target.data.parentRelationType))
            .on("mouseout", () => this._handleNodeMouseOutTooltip())
            .classed("link-text", true);
        this._resetZoom();
    }
    private _handleNodeMouseOverTooltip(text: string): void {
        if (this.isFullScreen) {
            this._renderer.setStyle(this._tooltipFullScreen.nativeElement, "display", "block");
            this._renderer.setProperty(this._tooltipFullScreen.nativeElement, "innerHTML", text);
        } else {
            if (this.cursorX > this._boundingBox().width - 32 * 2 && this.cursorY > this._boundingBox().height - 32 * 2) {
                return;
            }
            this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, "display", "block");
            this._renderer.setProperty(this._tooltipSmallScreen.nativeElement, "innerHTML", text);
        }
    }
    private _handleNodeMouseOutTooltip(): void {
        if (this.isFullScreen) {
            this._renderer.setProperty(this._tooltipFullScreen.nativeElement, "innerHTML", "");
            this._renderer.setStyle(this._tooltipFullScreen.nativeElement, "display", "none");
        } else {
            this._renderer.setProperty(this._tooltipSmallScreen.nativeElement, "innerHTML", "");
            this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, "display", "none");
        }
    }

    private _addResourceTypeToDisplayNameIfNeeded(displayName: string, node: InventoryGraphNode) {
        if (node.nodeType !== InventoryGraphNodeType.resource) return displayName;
        return (
            displayName +
            `<tspan x="0" dy="11" style="font-size: 10px; fill:rgba(105, 104, 121, 1)">${buildResourceTypeDisplayName(node.resource.type)}</tspan>`
        );
    }

    private _changeDisplayNameToBeMultipleLinesIfTooLong(originalDisplayName: string) {
        const originalDisplayNameLength = originalDisplayName.length;
        const sizeOfDisplayNameMax = 24;
        let firstLine = originalDisplayName;

        if (originalDisplayNameLength >= sizeOfDisplayNameMax) {
            for (let i = sizeOfDisplayNameMax; i > 0; i--) {
                if (originalDisplayName[i] === " ") {
                    firstLine = originalDisplayName.slice(0, i);
                    break;
                }
            }
            if (firstLine.length > sizeOfDisplayNameMax) {
                firstLine = firstLine.slice(0, sizeOfDisplayNameMax - 3) + "...";
            } else {
                firstLine = firstLine + "...";
            }
        }
        return `<tspan x="0" dy="0">${firstLine}</tspan>`;
    }

    private _resetZoom() {
        const parentElement = this._svgContainer?.node()?.parentElement;
        if (parentElement == null) return;

        const fullScreen = parentElement === this._popup.nativeElement;
        // For full screen, we want to take the final size post animation
        const containerBoundingBox = fullScreen ? fullScreenBoundingBox() : parentElement.getBoundingClientRect();

        const scaleX = containerBoundingBox.width / this._graphWidth;
        const scaleY = containerBoundingBox.height / this._graphHeight;
        const scale = Math.max(minZoom, Math.min(scaleX, scaleY, maxInitialZoom));
        const translateX = (containerBoundingBox.width - this._graphWidth * scale) / 2;
        const translateY = (containerBoundingBox.height - this._graphHeight * scale) / 2;
        const graphZoom = zoom<SVGSVGElement, InventoryGraphNode>().extent([
            [0, 0],
            [300, 300],
        ]);

        this._graphSvg
            .attr("transform", `translate(${translateX} ${translateY}) scale(${scale})`)
            .attr("opacity", 0)
            .transition()
            .ease(easeQuadOut)
            .duration(500)
            .attr("opacity", 1)
            .on("end", () =>
                this._svgContainer
                    .call(graphZoom.scaleExtent([minZoom, maxZoom]).on("zoom", (event) => this._graphSvg.attr("transform", event.transform)))
                    .call(graphZoom.transform, zoomIdentity.translate(translateX, translateY).scale(scale)),
            );
    }

    private _handleNodeMouseOver(nodes: NodeSelection, currentNode: GraphHierarchyNode, enable: boolean) {
        if (currentNode.data.resource.id === this.resourceId)
            nodes
                .select("circle")
                .filter((node) => node.data.resource.id === currentNode.data.resource.id && node.data.resource.id === this.resourceId)
                .classed("root-node", !enable);
        nodes
            .select("circle")
            .filter((node) => node.data.resource.id === currentNode.data.resource.id)
            .transition()
            .duration(180)
            .attr("r", radius + (enable ? 3 : -3))
            .attr("opacity", enable ? 1 : 0);
    }

    private _onNodeClicked(event: PointerEvent, node: GraphHierarchyNode) {
        if (this.isIssueComponent) {
            return;
        }
        event.stopPropagation();
        if (this._inventoryGraphNodeTypeToCategory[node.data.nodeType] === InventoryGraphNodeCategory.virtualNode) {
            if (node.data.nodeType === InventoryGraphNodeType.publicAccess)
                this._router.navigate(["app/inventory"], {
                    queryParams: {
                        q: "publicAccess:true",
                    },
                });
            else if (node.data.nodeType === InventoryGraphNodeType.orgWideAccess)
                this._router.navigate(["app/inventory"], {
                    queryParams: {
                        q: "orgWideAccess:true",
                    },
                });
            return;
        }

        if (node.data.resource.id.startsWith("merged")) {
            if (
                node.parent &&
                this.mergedGraph.mergedGraphDictionary[node.parent?.data.resource.id][node.data.parentRelationType][node.data.resource.type]["false"]
                    .areMerged
            ) {
                this.mergedGraph.mergedGraphDictionary[node.parent?.data.resource.id][node.data.parentRelationType][node.data.resource.type][
                    "false"
                ].areMerged = false;
                this._rebuildGraph();
                return;
            }
        }

        this._router.navigate([], {
            queryParams: {
                integration: this.integrationId,
                resource: node.data.resource.id,
                violation: null,
            },
            queryParamsHandling: "merge",
        });
    }

    private _rebuildGraph() {
        const startIsFullScreen = this.isFullScreen;
        if (this.isFullScreen) {
            this.setFullScreen();
        }
        const svgGraph = document.getElementById("graph-svg");
        svgGraph?.remove();
        this._buildGraph();
        if (startIsFullScreen) {
            this.setFullScreen(true);
        }
    }

    public expandAllNodes() {
        Object.keys(this.mergedGraph.mergedGraphDictionary).forEach((nodeId) => {
            Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId]).forEach((parentRelationType) => {
                Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType]).forEach((resourceType) => {
                    Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType]).forEach((hasChild) => {
                        const nodes = this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType][hasChild];
                        if (hasChild === "false") {
                            nodes.areMerged = false;
                        }
                    });
                });
            });
        });

        this._rebuildGraph();
    }

    public collapseAllNodes() {
        Object.keys(this.mergedGraph.mergedGraphDictionary).forEach((nodeId) => {
            Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId]).forEach((parentRelationType) => {
                Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType]).forEach((resourceType) => {
                    Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType]).forEach((hasChild) => {
                        const nodes = this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType][hasChild];
                        if (hasChild === "false") {
                            nodes.areMerged = true;
                        }
                    });
                });
            });
        });

        this._rebuildGraph();
    }

    public setFullScreen(fullScreen = !this.isFullScreen) {
        if (this.isFullScreen === fullScreen) return;

        this.isFullScreen = fullScreen;

        this._updatePositionBindings(this._boundingBox());

        const [source, target] = this.isFullScreen ? [this._graph, this._popup] : [this._popup, this._graph];
        this._reparentGraph(source, target);

        this._resetZoom();
    }

    private _reparentGraph(source: ElementRef, target: ElementRef) {
        const svgElement = source.nativeElement.querySelector("svg");
        this._renderer.removeChild(source.nativeElement, svgElement);
        this._renderer.appendChild(target.nativeElement, svgElement);
    }

    private _updatePositionBindings(rect: DOMRect) {
        this.left = rect.x;
        this.top = rect.y;
        this.width = rect.width;
        this.height = rect.height;
    }

    @HostListener("window:keydown", ["$event"])
    public onKeyEvent(event: KeyboardEvent) {
        switch (event.key) {
            // case 'Enter':
            //     event.stopImmediatePropagation()
            //     this.setFullScreen(true)
            //     break

            case "Escape":
                event.stopImmediatePropagation();
                this.setFullScreen(false);
                break;
        }
    }

    private _isRootNode = (node: GraphHierarchyNode) => node.data.resource.id === this.resourceId;
}
